import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { AlertService } from '../../../../services/alert.service';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CampaignsService } from '../../../../services/campaings.service';
import { useNavigate } from 'react-router-dom';
import { defaultValues, stepsFlows } from '../constants/data.constants';
import Button from '../../../../components/forms/button.component';
import Icon from '../../../../components/forms/icon.component';
import Navigate from '../../../../components/cards/header-navigate.component';
import Header from '../../../../components/layout/header.component';
import Asaid from '../../../../components/layout/asaid.component';
import Footer from '../../../../components/layout/footer.component';
import useBgPages from '../../../../hooks/use-bg-pages.hook';
import MockupRCS from '../../../../components/mockups/android.component';
import MockupWA from '../../../../components/mockups/iphone.componente';
import MockupSMS from '../../../../components/mockups/iphone-sms.component';
import useCamapignsStructures from '../../../../hooks/use-structure-campaigns.hook';
import party from 'party-js';
import LogData from '../../../../components/logs/log-data.component';

import Stepper1 from '../components/stepper-1-component';
import Stepper2 from '../components/stepper-2-component';
import Stepper3 from '../components/stepper-3-component';
import Stepper4 from '../components/stepper-4-flow.component';
import ChannelSMS from '../components/channels/channel-sms.component';
import ChannelRCS from '../components/channels/channel-rcs.component';
import ChannelWA from '../components/channels/channel-wa-component';
import ChannelMail from '../components/channels/channel-email.component';

function FlowCampaigns() {
  useBgPages();
  const { t } = useTranslation();
  const { structureData } = useCamapignsStructures();
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const methods = useForm({ mode: 'onChange', defaultValues });
  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting }
  } = methods;

  const channelFlows = watch('channelFlows') as string[];
  const [dynamicSteps, setDynamicSteps] = useState<string[]>([]);
  const [dynamicStepValidations, setDynamicStepValidations] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [stepValidations, setStepValidations] = useState<boolean[]>([]);

  const handleCharacterCountChange = useCallback(
    (count: number) => {
      setValue('totalCaracteresSms', count);
    },
    [setValue]
  );
  const handleOnSubmit = async (formData: any) => {
    const isConfirmed = await AlertService.question(
      '¡ Atención !',
      '¿Deseas guardar esta campaña con la información ingresada?',
      () => Promise.resolve(true),
      'Guardar campaña'
    );

    if (!isConfirmed) return;
    try {
      const cleanedData = structureData(formData);

      if (cleanedData?.error) {
        console.error('Error:', cleanedData);
        return;
      }

      await CampaignsService.saveCampaigns(cleanedData);
      AlertService.toastSuccess('¡ Éxito !, tu campaña se guardo correctamente');
      if (buttonRef.current) {
        party.confetti(buttonRef.current, {
          count: 200,
          speed: 450,
          spread: 190
        });
      }
      LogData(cleanedData, 'saveCampaignFallBack-594956963495', window.location.href);
      setTimeout(() => {
        navigate('/sendCampaigns/resume', { state: { structure: cleanedData } });
      }, 1500);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const formValues = useMemo(() => {
    const values = watch();
    return {
      headersOptions: values.headersOptions,
      masks: values.masks,
      complementsSwitch: values.complementsSwitch,
      complements: values.complements,
      phone_number: values.phone_number,
      msgWhatsAppLink: values.msgWhatsAppLink,
      urlLink: values.urlLink,
      msgSMS: values.msgSMS,
      accountsWa: values.accountsWa,
      templatesWa: values.templatesWa,
      typeRCS: values.typeRCS,
      agentsRCS: values.agentsRCS,
      templatesRCS: values.templatesRCS,
      accountsEmail: values.accountsEmail,
      templatesEmail: values.templatesEmail,
      events: values.events
    };
  }, [watch()]);

  const setStepValidity = useCallback((index: number, isValid: boolean) => {
    setStepValidations((prev) => {
      if (prev[index] === isValid) return prev;
      const newValidations = [...prev];
      newValidations[index] = isValid;
      return newValidations;
    });
  }, []);

  const setDynamicStepValidity = useCallback((channel: string, isValid: boolean) => {
    setDynamicStepValidations((prev) => {
      if (prev[channel] === isValid) return prev;
      return {
        ...prev,
        [channel]: isValid
      };
    });
  }, []);

  const stepsComponents = useMemo(() => {
    const components = [
      <Stepper1 key="step1" onValidationChange={(isValid) => setStepValidity(0, isValid)} />,
      <Stepper2 key="step2" onValidationChange={(isValid) => setStepValidity(1, isValid)} />,
      <Stepper3 key="step3" onValidationChange={(isValid) => setStepValidity(2, isValid)} />,
      <Stepper4 key="step4" onValidationChange={(isValid) => setStepValidity(3, isValid)} />
    ];

    if (stepValidations.length !== components.length) {
      setStepValidations(Array(components.length).fill(false));
    }

    return components;
  }, [setStepValidity, stepValidations.length]);

  useEffect(() => {
    if (!Array.isArray(channelFlows)) return;

    setDynamicSteps((prevSteps) => {
      const newSteps = [...channelFlows];

      if (JSON.stringify(prevSteps) === JSON.stringify(newSteps)) {
        return prevSteps;
      }

      setDynamicStepValidations((prevValidations) => {
        const newValidations = { ...prevValidations };
        prevSteps.forEach((step) => {
          if (!newSteps.includes(step)) {
            delete newValidations[step];
          }
        });
        return newValidations;
      });

      return newSteps;
    });
  }, [channelFlows]);

  const memoizedValidationInputs = useMemo(
    () => ({
      mask: formValues.masks,
      message: formValues.msgSMS,
      complementsActive: formValues.complementsSwitch,
      complementsSelected: formValues.complements,
      phoneNumber: formValues.phone_number,
      messageWaLink: formValues.msgWhatsAppLink,
      link: formValues.urlLink,
      typeRcs: formValues.typeRCS,
      agentsRcs: formValues.agentsRCS,
      templateRcs: formValues.templatesRCS,
      accountWa: formValues.accountsWa,
      templateWa: formValues.templatesWa,
      accountsEmail: formValues.accountsEmail,
      templatesEmail: formValues.templatesEmail,
      events: formValues.events
    }),
    [formValues]
  );

  const validateChannelEvents = useCallback((channel: string, events: any) => {
    if (!events?.[channel]) return true; // Si no hay eventos, es válido

    const channelEvents = events[channel];
    if (channelEvents.failed) {
      if (!channelEvents.failedChannel) {
        return false;
      }
    }
    if (['wa', 'rcs', 'mail'].includes(channel) && channelEvents.followUp) {
      if (!channelEvents.followUpTime || !channelEvents.followUpTimeUnit) {
        return false;
      }
      if (!channelEvents.followUpOnSent && !channelEvents.followUpOnDelivered) {
        return false;
      }
      if (channelEvents.followUpOnSent && !channelEvents.followUpTemplateSent) {
        return false;
      }
      if (channelEvents.followUpOnDelivered && !channelEvents.followUpTemplateDelivered) {
        return false;
      }
    }
    if (['wa', 'rcs'].includes(channel) && channelEvents.changeChannel) {
      if (!channelEvents.changeChannelTime || !channelEvents.changeChannelTimeUnit) {
        return false;
      }
      if (!channelEvents.changeChannelOnSent && !channelEvents.changeChannelOnDelivered) {
        return false;
      }
      if (channelEvents.changeChannelOnSent && !channelEvents.changeChannelTargetSent) {
        return false;
      }
      if (channelEvents.changeChannelOnDelivered && !channelEvents.changeChannelTargetDelivered) {
        return false;
      }
    }

    return true;
  }, []);
  useEffect(() => {
    dynamicSteps.forEach((step) => {
      let isValid = false;

      switch (step) {
        case 'sms': {
          const baseValidation =
            !!memoizedValidationInputs.mask && !!memoizedValidationInputs.message;
          if (memoizedValidationInputs.complementsActive) {
            const isComplementSelected = !!memoizedValidationInputs.complementsSelected;

            if (!isComplementSelected) {
              isValid = false;
              break;
            }
            const complementValidators: Record<string, boolean> = {
              WHATSAPP_LINK:
                !!memoizedValidationInputs.phoneNumber && !!memoizedValidationInputs.messageWaLink,
              LINK: !!memoizedValidationInputs.link
            };

            const isComplementValid =
              complementValidators[memoizedValidationInputs.complementsSelected] ?? false;
            const smsEventsValid = validateChannelEvents('sms', memoizedValidationInputs.events);
            isValid = baseValidation && isComplementSelected && isComplementValid && smsEventsValid;
          } else {
            const smsEventsValid = validateChannelEvents('sms', memoizedValidationInputs.events);
            isValid = baseValidation && smsEventsValid;
          }
          break;
        }

        case 'rcs': {
          const baseValidation =
            !!memoizedValidationInputs.typeRcs &&
            !!memoizedValidationInputs.agentsRcs &&
            !!memoizedValidationInputs.templateRcs;
          const rcsEventsValid = validateChannelEvents('rcs', memoizedValidationInputs.events);
          isValid = baseValidation && rcsEventsValid;
          break;
        }

        case 'wa': {
          const baseValidation =
            !!memoizedValidationInputs.accountWa && !!memoizedValidationInputs.templateWa;
          const waEventsValid = validateChannelEvents('wa', memoizedValidationInputs.events);
          isValid = baseValidation && waEventsValid;
          break;
        }

        case 'email': {
          const baseValidation =
            !!memoizedValidationInputs.accountsEmail && !!memoizedValidationInputs.templatesEmail;
          const mailEventsValid = validateChannelEvents('mail', memoizedValidationInputs.events);
          isValid = baseValidation && mailEventsValid;
          break;
        }

        default:
          isValid = false;
      }

      setDynamicStepValidity(step, isValid);
    });
  }, [dynamicSteps, memoizedValidationInputs, setDynamicStepValidity, validateChannelEvents]);

  const totalSteps = stepsComponents.length + dynamicSteps.length;
  const isCurrentStepValid = useMemo(() => {
    return currentStep < stepsComponents.length
      ? stepValidations[currentStep] ?? false
      : dynamicStepValidations[dynamicSteps[currentStep - stepsComponents.length]] ?? false;
  }, [currentStep, stepsComponents.length, stepValidations, dynamicStepValidations, dynamicSteps]);

  const nextStep = useCallback(() => {
    if (currentStep < totalSteps - 1 && isCurrentStepValid) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, totalSteps, isCurrentStepValid]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const renderDynamicStep = useCallback(
    (channel: string) => {
      const renderChannelComponent = () => {
        switch (channel) {
          case 'sms':
            return <ChannelSMS />;
          case 'wa':
            return (
              <ChannelWA onValidationChange={(isValid) => setDynamicStepValidity('wa', isValid)} />
            );
          case 'rcs':
            return <ChannelRCS />;
          case 'email':
            return <ChannelMail />;
          default:
            return null;
        }
      };

      return <div className="flex-column">{renderChannelComponent()}</div>;
    },
    [setDynamicStepValidity]
  );

  const renderAllSteps = useCallback(() => {
    return (
      <>
        {stepsComponents.map((Component, index) => (
          <div
            key={`step-fixed-${index}`}
            style={{ display: currentStep === index ? 'block' : 'none' }}
          >
            {Component}
          </div>
        ))}
        {dynamicSteps.map((channel, idx) => {
          const stepIndex = stepsComponents.length + idx;
          return (
            <div
              key={`step-dynamic-${channel}`}
              style={{ display: currentStep === stepIndex ? 'block' : 'none' }}
            >
              {renderDynamicStep(channel)}
            </div>
          );
        })}
      </>
    );
  }, [stepsComponents, currentStep, dynamicSteps, renderDynamicStep]);

  useEffect(() => {
    setValue('typeCampaign', 'FLOW');
  }, []);

  return (
    <div className="page d-flex flex-row flex-column-fluid">
      <Asaid />
      <div className="wrapper d-flex min-vh-100 flex-column flex-row-fluid" id="kt_wrapper">
        <Header />
        <div
          className="content fs-6 d-flex flex-column flex-column-fluid"
          id="kt_content"
          style={{
            backgroundImage: "url('/assets/media/img/bg-campaigns-2.png')",
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="post fs-6 d-flex flex-column-fluid mt-7" id="kt_post">
            <div className="container-xxl">
              <div className="mb-16">
                <Navigate
                  btnBack
                  title={
                    <>
                      <Icon icon="ki-medal-star" size="fs-3x" type="primary" />
                      <span className="ms-2">Campañas</span>
                    </>
                  }
                  titleBolder="Flujo de mensajes"
                  subtitle="Ingresa los datos necesarios de tu campaña."
                  breadcrumbItems={['Modalidades de campañas', 'Registro']}
                />
              </div>

              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(handleOnSubmit)}>
                  <div className="stepper">
                    <div className="d-flex justify-content-center align-items-center px-5 py-10">
                      <div className="row w-100 g-5">
                        {/* Columna lateral: Stepper + Mockups */}
                        <div className="col-12 col-md-4 col-lg-4">
                          <div className="stepper stepper-pills stepper-column d-flex flex-column flex-lg-row mb-20">
                            <div className="stepper-nav">
                              {[
                                ...stepsFlows.map((step, i) => ({
                                  ...step,
                                  isCurrent: currentStep === i
                                })),
                                ...dynamicSteps.map((ch, idx) => ({
                                  number: stepsFlows.length + idx + 1,
                                  title: `Canal: ${
                                    ch === 'sms'
                                      ? 'SMS'
                                      : ch === 'wa'
                                        ? 'WhatsApp'
                                        : ch === 'rcs'
                                          ? 'RCS'
                                          : ch
                                  }`,
                                  description: 'Configura tu canal.',
                                  isCurrent: currentStep === stepsFlows.length + idx
                                }))
                              ].map((step, index) => (
                                <div
                                  key={`step-${index}`}
                                  className={`stepper-item me-5 ${step.isCurrent ? 'current' : ''}`}
                                  data-kt-stepper-element="nav"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    const isValid =
                                      index < stepsFlows.length
                                        ? stepValidations[index]
                                        : dynamicStepValidations[
                                            dynamicSteps[index - stepsFlows.length]
                                          ] ?? false;

                                    if (index <= currentStep || isValid) {
                                      setCurrentStep(index);
                                    }
                                  }}
                                >
                                  <div className="stepper-wrapper d-flex align-items-center">
                                    <div className="stepper-icon w-40px h-40px">
                                      {index < currentStep ? (
                                        <Icon
                                          icon="ki-check"
                                          size="fs-1"
                                          type="primary"
                                          clasName="fw-bolder"
                                        />
                                      ) : (
                                        <span className="stepper-number">{step.number}</span>
                                      )}
                                    </div>
                                    <div className="stepper-label">
                                      <h3 className="stepper-title text-dark fw-bolder">
                                        {step.title}
                                      </h3>
                                      <div className="stepper-desc text-dark">
                                        {step.description}
                                      </div>
                                    </div>
                                  </div>
                                  {index !== totalSteps - 1 && (
                                    <div className="stepper-line h-40px" />
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Mockups */}
                          <div className="sticky-box">
                            {currentStep >= stepsComponents.length &&
                              (() => {
                                const channel = dynamicSteps[currentStep - stepsComponents.length];
                                switch (channel) {
                                  case 'sms':
                                    return (
                                      <MockupSMS
                                        message={watch('msgSMS')}
                                        variables={formValues.headersOptions}
                                        shortURL={
                                          watch('complements') === 'WHATSAPP_LINK' ||
                                          watch('complements') === 'LINK'
                                        }
                                        onCharacterCountChange={handleCharacterCountChange}
                                      />
                                    );
                                  case 'rcs':
                                    return (
                                      <MockupRCS
                                        payload={watch('previewTemplateRcs')}
                                        type={watch('previewTemplateRcs')?.type?.toLowerCase()}
                                        exmplesCampaigns={watch('headersOptions')}
                                      />
                                    );
                                  case 'wa':
                                    return (
                                      <MockupWA
                                        payload={watch('previewTemplateWa')}
                                        vars={watch('vars')}
                                        exmplesCampaigns={watch('headersOptions')}
                                      />
                                    );
                                  default:
                                    return null;
                                }
                              })()}
                          </div>
                        </div>

                        <div className="col-12 col-md-8 col-lg-8">
                          <div className="card animate-fadeInUp animate-delay-1">
                            <div className="card-body">
                              <div className="flex-column">{renderAllSteps()}</div>
                            </div>

                            <div className="card-footer d-flex justify-content-end gap-3">
                              <Button
                                type="button"
                                onClick={prevStep}
                                disabled={currentStep === 0}
                                className="btn btn-lg btn-secondary hover-scale"
                              >
                                <Icon icon="ki-left" size="fs-1" />
                                {t('general.back')}
                              </Button>

                              {currentStep < totalSteps - 1 ? (
                                <Button
                                  type="button"
                                  onClick={nextStep}
                                  disabled={!isCurrentStepValid}
                                  className="btn btn-lg btn-primary hover-scale"
                                >
                                  {t('general.continue')} <Icon icon="ki-right" size="fs-1" />
                                </Button>
                              ) : (
                                <Button
                                  type="submit"
                                  ref={buttonRef}
                                  disabled={!isCurrentStepValid}
                                  isLoading={isSubmitting}
                                  className="btn btn-lg btn-primary hover-scale"
                                >
                                  {t('general.save')}{' '}
                                  <Icon icon="ki-send" size="fs-1" className="ms-2" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}

export default FlowCampaigns;
